<template>
  <loader v-if="loadingProducts" />
  <div v-else :data-qa="`${quoteNamespace}_dealer-accessory-catalog`">
    <h5>{{ i18n.t('/pdf/categories/categories dealeraccessories label') }}</h5>

    <div
      v-if="catalogLoadFailed"
      class="dealer-accessory-catalog__error"
      role="alert"
      data-qa="accessory-catalog-load-error"
    >
      <span>{{ i18n.t('/AccessoryCatalog/LoadFailed') }}</span>
      <app-button
        data-qa="accessory-catalog-retry"
        :disabled="verifyingCatalog"
        @click="verifyCatalog"
      >
        {{ i18n.t('/AccessoryCatalog/Retry') }}
      </app-button>
    </div>

    <chosen-accessories-card
      :rows="chosenRows"
      :is-editable="isEditable"
      @edit="openEdit"
      @remove="removeFromSelected"
      @open-catalog="catalogOpen = true"
      @new-accessory="openNewAccessory"
    />

    <teleport to="body">
      <accessory-catalog-overlay
        v-if="catalogOpen"
        :products="products"
        :selected-ids="selectedIds"
        :quote-rows="selectedProducts"
        :context-label="contextLabel"
        :is-editable="isEditable"
        :vat="vat"
        @toggle="toggle"
        @close="catalogOpen = false"
      />
    </teleport>

    <teleport to="body">
      <action-price-dialog
        v-if="priceChoiceAccessory"
        :accessory="priceChoiceAccessory"
        :vat="vat"
        :now="now"
        @choose="addWithChosenPrice"
        @close="priceChoiceAccessory = null"
      />
    </teleport>

    <modal
      v-if="editOpen"
      id="accessory-catalog-edit"
      :title="editTitle"
      track-category="service flow"
      @close="closeEdit"
    >
      <edit-product
        :product="editAccessory"
        :categories="categories"
        :original-product="originalFor(editAccessory)"
        @close-edit-modal="closeEdit"
      />
    </modal>
  </div>
</template>

<script>
import AppButton from '@/components/generic/presentational/atoms/appButton/AppButton.vue'
import Loader from '@/components/generic/presentational/atoms/loader/Loader.vue'
import Modal from '@/components/generic/presentational/molecules/modal/Modal.vue'
import EditProduct from '@/components/generic/presentational/molecules/editProduct/EditProduct.vue'
import ChosenAccessoriesCard from '@/components/generic/presentational/organisms/accessoryCatalog/ChosenAccessoriesCard.vue'
import AccessoryCatalogOverlay from '@/components/generic/presentational/organisms/accessoryCatalog/AccessoryCatalogOverlay.vue'
import ActionPriceDialog from '@/components/generic/presentational/molecules/accessoryCatalog/ActionPriceDialog.vue'
import DealerAccessories from 'api-client/DealerAccessory.api'
import HandleAccessoryEvents from '@/mixins/handleAccessoryEvents.js'
import AdditionalOrder from '@/mixins/additionalOrder.js'
import catalogClock from '@/mixins/catalogClock'
import {
  PRICE_CHOICE_ACTION,
  VAT_EXCL,
  VAT_INCL,
  actionPriceDiffers,
  withActionPrice,
} from '@/helpers/ponCatalog'
import { mapGetters, mapState } from 'vuex'

export default {
  components: {
    AppButton,
    Loader,
    Modal,
    EditProduct,
    ChosenAccessoriesCard,
    AccessoryCatalogOverlay,
    ActionPriceDialog,
  },
  mixins: [HandleAccessoryEvents, AdditionalOrder, catalogClock],
  data() {
    return {
      i18n: window.VUE_CLI_EXPORTS.plugins.i18n,
      categories: ['DealerAccessories'],
      catalogOpen: false,
      editOpen: false,
      // The quote row being edited; null while creating a new accessory
      editAccessory: null,
      priceChoiceAccessory: null,
      catalogLoadFailed: false,
      verifyingCatalog: false,
    }
  },
  computed: {
    ...mapGetters({
      showInclPrice: 'quote/isPriceShowIncl',
    }),
    /**
     * Which price the catalog shows where it has room for only one. Both prices
     * are shown side by side on the quote, so only the excluding-VAT mode moves
     * the catalog off the incl. price.
     */
    vat() {
      return this.showInclPrice ? VAT_INCL : VAT_EXCL
    },
    // Dynamic namespaces aren't supported by mapGetters
    ...mapState({
      quoteAccessoriesWithCategories(state, getters) {
        return getters[this.quoteNamespace + '/accessoriesWithCategories']
      },
      isEditable(state, getters) {
        return !getters[this.quoteNamespace + '/quoteStatusIsSalesOrder']
      },
      // The accessories module is registered at the root of the store, not under
      // the quote, so it is never reachable through the quote namespace.
      loadingProducts(state, getters) {
        return getters['accessories/loading']
      },
      accessoriesWithCategories(state, getters) {
        return getters['accessories/accessoriesWithCategories']
      },
      getMatchingAccessory(state, getters) {
        return getters['accessories/getMatchingAccessory']
      },
      configuration(state, getters) {
        return getters['configuration/configuration']
      },
    }),
    selectedProducts() {
      return this.quoteAccessoriesWithCategories(this.categories)
    },
    selectedIds() {
      return this.selectedProducts.map((p) => p.OptionType_ID)
    },
    products() {
      const catalog = this.accessoriesWithCategories(this.categories) || []
      if (!this.additionalQuote) return catalog

      // An additional order offers what the original quote does not already carry
      const parentRows =
        this.$store.getters['quote/accessoriesWithCategories'](this.categories) || []
      const parentIds = parentRows.map((x) => x.ID)
      return catalog.filter((x) => !parentIds.includes(x.ID))
    },
    /**
     * The store finished loading and produced nothing. That is either a car
     * without dealer accessories or a fetch that failed — the store keeps
     * `all: []` either way, which is what `verifyCatalog` is here to tell apart.
     */
    catalogIsEmpty() {
      return !this.loadingProducts && this.products.length === 0
    },
    chosenRows() {
      return this.selectedProducts.map((accessory) => ({
        accessory,
        catalogItem: this.getMatchingAccessory(accessory.OptionType_ID),
      }))
    },
    contextLabel() {
      const configuration = this.configuration || {}
      return [
        configuration.CgiModelGroupDescription,
        configuration.CgiTrimLineDescription || configuration.TrimlineName,
      ]
        .filter(Boolean)
        .join(' · ')
    },
    editTitle() {
      return this.editAccessory
        ? this.i18n.t('/EditProduct/ChangeAccessoryButton')
        : this.i18n.t('/EditProduct/AddAccessoryButton')
    },
  },
  watch: {
    catalogIsEmpty: {
      immediate: true,
      handler(isEmpty) {
        if (isEmpty) this.verifyCatalog()
        else this.catalogLoadFailed = false
      },
    },
  },
  methods: {
    /**
     * Nothing in the accessories store records that a fetch failed: the api
     * client swallows the rejection, the action bails out and the module is left
     * holding the empty list it started with. So the one case where the
     * difference is visible to the user — an empty catalog — is asked again
     * here, and answered against the request itself instead of against the
     * store. A rejection and the client's swallowed `undefined` both mean the
     * catalog could not be loaded; an array (or a 404's `null`) means the car
     * really has no dealer accessories.
     */
    async verifyCatalog() {
      if (this.verifyingCatalog) return
      this.verifyingCatalog = true
      this.catalogLoadFailed = false
      try {
        const accessories = await this.fetchCatalog()
        if (accessories === undefined) throw new Error('accessories request failed')
        await this.$store.dispatch('accessories/setAll', accessories || [])
      } catch {
        this.catalogLoadFailed = true
      } finally {
        this.verifyingCatalog = false
      }
    },
    /** The catalog belongs to the car on the original quote, also for an additional order */
    fetchCatalog() {
      const car = this.$store.getters['quote/car'] || {}
      // New stock has its own route; getAll covers configurations and stockcars
      if (car.StockId) return DealerAccessories.getForStockId(car.StockId)
      return DealerAccessories.getAll(car.ConfigurationCode, car.StockCarId)
    },
    toggle(accessory) {
      const quoteRow = this.selectedProducts.find(
        (p) => p.OptionType_ID === accessory.OptionType_ID
      )
      if (quoteRow) {
        this.removeFromSelected(quoteRow)
      } else if (actionPriceDiffers(accessory, this.now)) {
        // Let the user pick between the Autoline price and the running action price
        this.priceChoiceAccessory = accessory
      } else {
        // addToSelected comes from HandleAccessoryEvents; the quote store strips the enrichment
        this.addToSelected(accessory)
      }
    },
    addWithChosenPrice(choice) {
      const accessory = this.priceChoiceAccessory
      this.priceChoiceAccessory = null
      this.addToSelected(
        choice === PRICE_CHOICE_ACTION ? withActionPrice(accessory, this.now) : accessory
      )
    },
    openEdit(quoteRow) {
      this.editAccessory = quoteRow
      this.editOpen = true
    },
    openNewAccessory() {
      // No product means EditProduct starts empty and adds a custom dealer accessory
      this.editAccessory = null
      this.editOpen = true
    },
    closeEdit() {
      this.editOpen = false
      this.editAccessory = null
    },
    originalFor(quoteRow) {
      if (!quoteRow) return null
      return this.getMatchingAccessory(quoteRow.OptionType_ID)
    },
  },
}
</script>

<style lang="scss" scoped>
.dealer-accessory-catalog__error {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  border: 1px solid $color-warning-border;
  border-radius: 0.25rem;
  background: $color-warning-light;
  color: $color-warning-dark;
  font-size: 0.9rem;
}
</style>
